import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import merge from 'lodash.merge';
import wretch from 'wretch';

import { IDashboardMeta } from './interfaces';
import DashboardContext from './DashboardContext';
import Dashboard from './Dashboard';
import Header from './Header';

export default function DashboardsLayout() {
  const { slug } = useParams();
  const [dashboardMeta, setDashboardMeta] = useState<
    IDashboardMeta | undefined
  >(undefined);

  const selectedDashboard = slug || dashboardMeta?.tabs[0].slug;

  useEffect(() => {
    wretch('/dashboards')
      .get()
      .json()
      .then((json: any) => {
        setDashboardMeta(json);
      });
  }, []);

  const theme = useMemo(() => {
    const thm = createTheme(
      merge(
        {
          breakpoints: {
            values: {
              xs: 0,
              sm: 0,
              md: 768,
              lg: 1200,
              xl: 1600,
            },
          },
          palette: { divider: '#c6c6c6' },
          typography: {
            fontFamily: `'Lato', 'Roboto', sans-serif`,
          },
        },
        dashboardMeta?.theme ?? {},
      ),
    );

    thm.typography.h1 = {
      fontWeight: 'normal',
      lineHeight: 1,
      [thm.breakpoints.up('xs')]: {
        fontSize: `1.5rem`,
      },
      [thm.breakpoints.up('md')]: {
        fontSize: `2rem`,
      },
      [thm.breakpoints.up('lg')]: {
        fontSize: `2.25rem`,
      },
      [thm.breakpoints.up('xl')]: {
        fontSize: `2.5rem`,
      },
    };

    return thm;
  }, [dashboardMeta?.theme]);

  const context = useMemo(() => {
    if (dashboardMeta) {
      return {
        ...dashboardMeta,
        selectedDashboard,
        theme,
      };
    }

    return null;
  }, [dashboardMeta, selectedDashboard, theme]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {dashboardMeta ? (
        <DashboardContext.Provider value={context}>
          <Header />
          <Box
            component="main"
            sx={{
              // keep this order or we lose the divider color
              borderTop: 2,
              borderColor: 'divider',
            }}
          >
            <Dashboard />
          </Box>
        </DashboardContext.Provider>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            height: '100vh',
            width: '100vw',
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </ThemeProvider>
  );
}
